import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Zap, ArrowRight, Package, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "./constants";
import { SplitLocationInput, formatLocation, parseLocation, validateLocation } from "@/pages/ProductMaster";

const BulkShelfRelocator = ({ onProductUpdated }) => {
  const [fromShelf, setFromShelf] = useState("");
  const [toShelf, setToShelf] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [relocating, setRelocating] = useState(false);
  const [done, setDone] = useState(null);
  const fromRef = useRef(null);

  useEffect(() => {
    fromRef.current?.focus();
  }, []);

  const handlePreview = async () => {
    if (!fromShelf.trim()) return;
    setLoading(true);
    setPreview(null);
    setDone(null);
    try {
      const { data } = await axios.get(`${API}/products/shelf/${fromShelf.trim().toUpperCase()}/products`);
      if (data.count === 0) {
        toast.error(`No products found on shelf ${fromShelf.toUpperCase()}`);
      } else {
        setPreview(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load shelf products");
    } finally {
      setLoading(false);
    }
  };

  const handleRelocate = async () => {
    if (!fromShelf.trim() || !toShelf.trim()) {
      toast.error("Enter both source and destination shelf");
      return;
    }
    if (fromShelf.trim().toUpperCase() === toShelf.trim().toUpperCase()) {
      toast.error("Source and destination are the same");
      return;
    }
    setRelocating(true);
    try {
      const { data } = await axios.post(`${API}/products/bulk-relocate-shelf`, {
        from_shelf: fromShelf.trim(),
        to_shelf: toShelf.trim(),
      });
      setDone(data);
      setPreview(null);
      toast.success(`Moved ${data.moved} products from ${data.from_shelf} to ${data.to_shelf}`);
      onProductUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to relocate products");
    } finally {
      setRelocating(false);
    }
  };

  const reset = () => {
    setFromShelf("");
    setToShelf("");
    setPreview(null);
    setDone(null);
    fromRef.current?.focus();
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-lg text-sm border border-blue-200">
        <p className="font-medium text-blue-800 mb-1">Bulk Shelf Relocate:</p>
        <p className="text-blue-700">Move all products from one shelf to another. Bin numbers stay the same.</p>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label className="text-sm font-medium">From Shelf</Label>
          <Input
            data-testid="from-shelf-input"
            ref={fromRef}
            value={fromShelf}
            onChange={(e) => setFromShelf(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handlePreview()}
            placeholder="e.g. I1-2"
            className="h-12 text-lg font-mono text-center"
          />
        </div>
        <ArrowRight className="h-8 w-8 text-gray-400 mb-1 shrink-0" />
        <div className="flex-1">
          <Label className="text-sm font-medium">To Shelf</Label>
          <Input
            data-testid="to-shelf-input"
            value={toShelf}
            onChange={(e) => setToShelf(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && (preview ? handleRelocate() : handlePreview())}
            placeholder="e.g. I1-3"
            className="h-12 text-lg font-mono text-center"
          />
        </div>
        <Button
          data-testid="preview-shelf-btn"
          onClick={handlePreview}
          disabled={loading || !fromShelf.trim()}
          variant="outline"
          className="h-12"
        >
          {loading ? "Loading..." : "Preview"}
        </Button>
      </div>

      {preview && (
        <div className="border-2 border-amber-300 rounded-lg p-4 bg-amber-50">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-5 w-5 text-amber-700" />
            <span className="font-bold text-amber-800">
              {preview.count} product{preview.count !== 1 ? 's' : ''} on shelf {preview.shelf}
            </span>
          </div>
          <div className="max-h-48 overflow-y-auto border rounded bg-white divide-y">
            {preview.products.map((p, i) => (
              <div key={p.id || i} className="px-3 py-2 text-sm flex justify-between items-center">
                <span className="truncate flex-1 font-medium">{p.name}</span>
                <span className="font-mono text-gray-500 ml-2 text-xs">
                  Bin: {p.bin_number || "—"}
                </span>
              </div>
            ))}
          </div>

          {toShelf.trim() && toShelf.trim().toUpperCase() !== fromShelf.trim().toUpperCase() && (
            <div className="mt-3 p-2 bg-amber-100 rounded flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <span className="text-sm text-amber-800">
                This will move <strong>{preview.count}</strong> product{preview.count !== 1 ? 's' : ''} from
                <strong className="font-mono"> {preview.shelf}</strong> to
                <strong className="font-mono"> {toShelf.trim().toUpperCase()}</strong>.
                Bin numbers will not change.
              </span>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={reset} className="flex-1">Cancel</Button>
            <Button
              data-testid="confirm-relocate-btn"
              onClick={handleRelocate}
              disabled={relocating || !toShelf.trim() || toShelf.trim().toUpperCase() === fromShelf.trim().toUpperCase()}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {relocating ? "Moving..." : `Move ${preview.count} Products`}
            </Button>
          </div>
        </div>
      )}

      {done && (
        <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
          <p className="font-bold text-green-800 mb-2">
            Moved {done.moved} products: {done.from_shelf} → {done.to_shelf}
          </p>
          <div className="max-h-32 overflow-y-auto border rounded bg-white divide-y">
            {done.products.map((p, i) => (
              <div key={i} className="px-3 py-1.5 text-sm flex justify-between">
                <span className="truncate flex-1">{p.name}</span>
                <span className="font-mono text-green-700 ml-2">{done.to_shelf}/{p.bin_number || "—"}</span>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={reset} className="mt-3 w-full" data-testid="relocate-another-btn">
            Relocate Another Shelf
          </Button>
        </div>
      )}
    </div>
  );
};

export const QuickLocationEditor = ({ open, onClose, products, onProductUpdated }) => {
  const [mode, setMode] = useState("single");  // "single" or "bulk"
  const [pluInput, setPluInput] = useState("");
  const [currentProduct, setCurrentProduct] = useState(null);
  const [shelfNumber, setShelfNumber] = useState("");
  const [binNumber, setBinNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [shouldFocusShelf, setShouldFocusShelf] = useState(false);
  const [holdShelf, setHoldShelf] = useState(false);
  const [heldShelfValue, setHeldShelfValue] = useState("");
  const pluInputRef = useRef(null);
  const shelfInputRef = useRef(null);
  const binInputRef = useRef(null);

  useEffect(() => {
    if (open && mode === "single" && pluInputRef.current) {
      pluInputRef.current.focus();
    }
  }, [open, mode]);

  useEffect(() => {
    if (!open) {
      setPluInput("");
      setCurrentProduct(null);
      setShelfNumber("");
      setBinNumber("");
      setRecentUpdates([]);
      setShouldFocusShelf(false);
    }
  }, [open]);

  const handlePluSearch = async (e) => {
    if (e.key === "Enter" && pluInput.trim()) {
      setSearching(true);
      try {
        let found = products.find(
          p => p.plu_code?.toLowerCase() === pluInput.toLowerCase() ||
               p.barcode?.toLowerCase() === pluInput.toLowerCase()
        );
        if (!found) {
          const response = await axios.get(`${API}/products/search`, {
            params: { q: pluInput.trim() }
          });
          if (response.data && response.data.length > 0) {
            found = response.data.find(
              p => p.plu_code?.toLowerCase() === pluInput.toLowerCase() ||
                   p.barcode?.toLowerCase() === pluInput.toLowerCase()
            );
            if (!found && response.data.length === 1) {
              found = response.data[0];
            }
          }
        }
        if (found) {
          setCurrentProduct(found);
          if (holdShelf && heldShelfValue) {
            setShelfNumber(heldShelfValue);
            setBinNumber("");
            setShouldFocusShelf(false);
            setTimeout(() => {
              binInputRef.current?.focus();
              binInputRef.current?.select();
            }, 100);
          } else if (found.location && found.location.includes('/')) {
            const { shelf, bin } = parseLocation(found.location);
            setShelfNumber(shelf);
            setBinNumber(bin);
            setShouldFocusShelf(true);
          } else {
            setShelfNumber(found.shelf_number || "");
            setBinNumber(found.bin_number || "");
            setShouldFocusShelf(true);
          }
        } else {
          toast.error(`Product not found: ${pluInput}`);
          setCurrentProduct(null);
        }
      } catch (error) {
        toast.error("Error searching for product");
      } finally {
        setSearching(false);
        setPluInput("");
      }
    }
  };

  const handleSave = async () => {
    if (!currentProduct) return;
    if (shelfNumber && binNumber) {
      const location = formatLocation(shelfNumber, binNumber);
      const validation = validateLocation(location);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    }
    setSaving(true);
    try {
      const location = formatLocation(shelfNumber, binNumber);
      await saveWithRetry(() => axios.put(`${API}/products/${currentProduct.id}`, {
        ...currentProduct,
        location: location,
        shelf_number: shelfNumber,
        bin_number: binNumber.padStart(4, '0')
      }));
      setRecentUpdates(prev => [{
        name: currentProduct.name,
        plu: currentProduct.plu_code,
        location: location || '-',
        time: new Date().toLocaleTimeString()
      }, ...prev.slice(0, 9)]);
      toast.success(`Updated location for ${currentProduct.name}`);
      onProductUpdated();
      if (holdShelf) {
        setHeldShelfValue(shelfNumber);
      }
      setCurrentProduct(null);
      if (!holdShelf) {
        setShelfNumber("");
      }
      setBinNumber("");
      setShouldFocusShelf(false);
      pluInputRef.current?.focus();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save location"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-700">
            <Zap className="h-5 w-5" />
            Quick Location Editor
          </DialogTitle>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex border-b mb-2">
          <button
            data-testid="tab-single"
            onClick={() => setMode("single")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              mode === "single"
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Single Product
          </button>
          <button
            data-testid="tab-bulk"
            onClick={() => setMode("bulk")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              mode === "bulk"
                ? "border-amber-600 text-amber-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Bulk Shelf Relocate
          </button>
        </div>

        {mode === "bulk" ? (
          <BulkShelfRelocator onProductUpdated={onProductUpdated} />
        ) : (
          <div className="space-y-4 py-2">
            <div className="bg-green-50 p-3 rounded-lg text-sm border border-green-200">
              <p className="font-medium text-green-800 mb-1">Keyboard-Only Workflow:</p>
              <ol className="text-green-700 space-y-1 list-decimal list-inside">
                <li>Scan/type PLU → <kbd className="px-1 bg-green-200 rounded">Enter</kbd></li>
                <li>Type Shelf (4 chars auto-advances to Bin)</li>
                <li>Type Bin → <kbd className="px-1 bg-green-200 rounded">Enter</kbd> saves & ready for next</li>
              </ol>
            </div>

            <div className={`flex items-center justify-between p-3 rounded-lg border ${holdShelf ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="holdShelf"
                  checked={holdShelf}
                  onChange={(e) => {
                    setHoldShelf(e.target.checked);
                    if (e.target.checked && shelfNumber) {
                      setHeldShelfValue(shelfNumber);
                    }
                  }}
                  className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="holdShelf" className="font-medium cursor-pointer">Hold Shelf</label>
                {holdShelf && heldShelfValue && (
                  <span className="ml-2 px-2 py-1 bg-amber-200 text-amber-800 rounded font-mono font-bold">
                    {heldShelfValue}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {holdShelf ? "Shelf locked - only enter Bin numbers" : "Enable to keep shelf for multiple products"}
              </span>
            </div>

            <div>
              <Label className="text-lg font-semibold">PLU Code / Barcode</Label>
              <Input
                ref={pluInputRef}
                value={pluInput}
                onChange={(e) => setPluInput(e.target.value)}
                onKeyDown={handlePluSearch}
                placeholder={searching ? "Searching..." : "Scan or type PLU/Barcode and press Enter..."}
                className="h-14 text-xl text-center mt-2"
                autoFocus
                disabled={searching}
              />
            </div>

            {currentProduct ? (
              <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                <div className="mb-4">
                  <div className="text-xl font-bold text-green-800">{currentProduct.name}</div>
                  <div className="text-sm text-green-600">
                    PLU: {currentProduct.plu_code} | Barcode: {currentProduct.barcode || "N/A"}
                  </div>
                  {currentProduct.location && (
                    <div className="text-sm text-green-700 mt-1">
                      Current Location: <span className="font-mono font-bold">{currentProduct.location}</span>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <Label className="text-sm font-medium">Location (Shelf/Bin)</Label>
                  <SplitLocationInput
                    shelfValue={shelfNumber}
                    binValue={binNumber}
                    onShelfChange={(v) => {
                      setShelfNumber(v.toUpperCase());
                      setShouldFocusShelf(false);
                      if (holdShelf) setHeldShelfValue(v.toUpperCase());
                    }}
                    onBinChange={setBinNumber}
                    onSave={handleSave}
                    size="large"
                    shelfRef={shelfInputRef}
                    binRef={binInputRef}
                    autoFocusShelf={shouldFocusShelf && !holdShelf}
                    shelfDisabled={holdShelf && heldShelfValue}
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentProduct(null);
                      setShelfNumber("");
                      setBinNumber("");
                      setShouldFocusShelf(false);
                      pluInputRef.current?.focus();
                    }}
                    className="flex-1"
                  >Cancel</Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || (!shelfNumber && !binNumber)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {saving ? "Saving..." : "Save & Next (Enter)"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Scan or enter a PLU code to start</p>
              </div>
            )}

            {recentUpdates.length > 0 && (
              <div>
                <Label className="text-sm text-gray-600">Recent Updates:</Label>
                <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg divide-y">
                  {recentUpdates.map((update, idx) => (
                    <div key={idx} className="p-2 text-sm flex justify-between items-center bg-gray-50">
                      <span className="font-medium truncate flex-1">{update.name}</span>
                      <span className="text-gray-600 font-mono mx-2">{update.location}</span>
                      <span className="text-xs text-gray-400">{update.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
